import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, ExternalLink, Trash2, Hash, Copy, Check, Calendar, Car } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { DayPicker } from 'react-day-picker';
import { format, parse } from 'date-fns';
import { enGB } from 'date-fns/locale';
import toast from 'react-hot-toast';
import type { FordShareEntry } from '@/api/campaigns';
import 'react-day-picker/style.css';

const FORDSHARE_URL_TEMPLATE =
  'https://ford.brandshare.net/global/advert/ActivityDetails.aspx?activityId=';

function getTodayLocalDate(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const d = parse(dateStr, 'yyyy-MM-dd', new Date());
    return format(d, 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
}

interface FordShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  entries: FordShareEntry[];
  onSave: (entries: FordShareEntry[]) => void;
  isLoading?: boolean;
}

function FordShareEntryRow({
  entry,
  onUpdate,
  onRemove,
  isLoading,
}: {
  entry: FordShareEntry;
  onUpdate: (updated: FordShareEntry) => void;
  onRemove: () => void;
  isLoading?: boolean;
}) {
  const [isEditingId, setIsEditingId] = useState(false);
  const [editId, setEditId] = useState(entry.id);
  const [copiedId, setCopiedId] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingId]);

  const handleIdSave = () => {
    const trimmed = editId.trim();
    if (!trimmed || !/^\d+$/.test(trimmed)) {
      setEditId(entry.id);
      setIsEditingId(false);
      return;
    }
    if (trimmed !== entry.id) {
      onUpdate({ ...entry, id: trimmed });
      toast.success('FordShare ID updated');
    }
    setIsEditingId(false);
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(entry.id);
    setCopiedId(true);
    toast.success('FordShare ID copied');
    setTimeout(() => setCopiedId(false), 1500);
  };

  const handleDateSelect = (day: Date | undefined) => {
    if (!day) return;
    const newDate = format(day, 'yyyy-MM-dd');
    if (newDate !== entry.delivery_date) {
      onUpdate({ ...entry, delivery_date: newDate });
      toast.success('Delivery date updated');
    }
    setDateOpen(false);
  };

  const selectedDate = entry.delivery_date
    ? parse(entry.delivery_date, 'yyyy-MM-dd', new Date())
    : undefined;

  const fullUrl = `${FORDSHARE_URL_TEMPLATE}${entry.id}`;

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl group hover:bg-slate-100/80 transition-colors">
      <div className="w-8 h-8 rounded-lg bg-slate-200/80 flex items-center justify-center flex-shrink-0">
        <Hash className="w-4 h-4 text-slate-500" />
      </div>

      {/* Left: ID + delivery date, left-aligned, consistent sizing */}
      <div className="flex-1 min-w-0 space-y-0.5">
        {/* ID row */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-400">ID:</span>
          {isEditingId ? (
            <input
              ref={inputRef}
              type="text"
              value={editId}
              onChange={e => setEditId(e.target.value)}
              onBlur={handleIdSave}
              onKeyDown={e => {
                if (e.key === 'Enter') handleIdSave();
                if (e.key === 'Escape') { setEditId(entry.id); setIsEditingId(false); }
              }}
              className="text-xs font-semibold text-slate-800 bg-white border border-slate-300 rounded px-1.5 py-0.5 w-28 focus:outline-none focus:ring-2 focus:ring-[#0014cc]/20 focus:border-[#0014cc]"
            />
          ) : (
            <span className="inline-flex items-center gap-1.5 group/id">
              <span
                className="text-xs font-semibold text-slate-800 tabular-nums cursor-pointer hover:text-[#0014cc] transition-colors"
                onClick={() => setIsEditingId(true)}
              >
                {entry.id}
              </span>
              <button
                onClick={handleCopyId}
                className="p-0.5 rounded text-slate-300 hover:text-slate-500 transition-colors opacity-0 group-hover/id:opacity-100"
              >
                {copiedId ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
              </button>
            </span>
          )}
        </div>

        {/* Delivery date row */}
        <Popover open={dateOpen} onOpenChange={setDateOpen}>
          <PopoverTrigger asChild>
            <button
              className="flex items-center gap-1.5 text-xs hover:text-slate-600 transition-colors"
              disabled={isLoading}
            >
              <span className="text-slate-400">Delivery date:</span>
              <Calendar className="w-3 h-3 text-slate-400" />
              <span className="text-slate-400">{entry.delivery_date ? formatDate(entry.delivery_date) : '—'}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <DayPicker
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              locale={enGB}
              className="p-3"
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Right: link + delete, vertically aligned */}
      <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-lg text-slate-300 hover:text-[#0014cc] hover:bg-blue-50 transition-colors"
              onClick={e => e.stopPropagation()}
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </TooltipTrigger>
          <TooltipContent className="text-[10px]" style={{ maxWidth: 'none', whiteSpace: 'nowrap' }}>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(fullUrl);
                  toast.success('URL copied');
                }}
                className="p-0.5 rounded hover:bg-white/20 transition-colors flex-shrink-0"
              >
                <Copy className="w-3 h-3" />
              </button>
              <span>{fullUrl}</span>
            </div>
          </TooltipContent>
        </Tooltip>
        <button
          onClick={onRemove}
          disabled={isLoading}
          className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export function FordShareModal({ isOpen, onClose, entries, onSave, isLoading }: FordShareModalProps) {
  const [newId, setNewId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleAdd = () => {
    const trimmed = newId.trim();
    if (!trimmed) return;

    if (!/^\d+$/.test(trimmed)) {
      setError('FordShare ID must be a number');
      return;
    }

    if (entries.some(e => e.id === trimmed)) {
      setError('This ID has already been added');
      return;
    }

    setError(null);
    onSave([...entries, { id: trimmed, delivery_date: getTodayLocalDate() }]);
    toast.success('FordShare ID added');
    setNewId('');
  };

  const handleRemove = (id: string) => {
    onSave(entries.filter(e => e.id !== id));
    toast.success('FordShare ID removed');
  };

  const handleUpdate = (index: number, updated: FordShareEntry) => {
    const newEntries = [...entries];
    newEntries[index] = updated;
    onSave(newEntries);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-[2px]">
      <div className="w-[480px] h-[520px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">

        {/* Header — dashboard ChartCard style with car icon */}
        <div className="px-5 py-3.5 border-b border-slate-200/50 bg-gradient-to-r from-slate-50/50 to-white/50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#dbeafe] rounded-xl flex items-center justify-center shadow-sm">
                <Car className="w-4.5 h-4.5" style={{ color: '#1e40af' }} />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900 leading-tight">FordShare Activity IDs</h2>
                <p className="text-xs text-slate-500 leading-tight">
                  {entries.length} {entries.length === 1 ? 'ID' : 'IDs'} linked
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Entries list */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {entries.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <Hash className="w-5 h-5 text-slate-300" />
              </div>
              <p className="text-sm font-medium text-slate-400">No FordShare IDs yet</p>
              <p className="text-xs text-slate-300 mt-1">Add one below to get started</p>
            </div>
          ) : (
            <div className="space-y-2">
              {entries.map((entry, i) => (
                <FordShareEntryRow
                  key={entry.id}
                  entry={entry}
                  onUpdate={updated => handleUpdate(i, updated)}
                  onRemove={() => handleRemove(entry.id)}
                  isLoading={isLoading}
                />
              ))}
            </div>
          )}
        </div>

        {/* Add new entry — footer style */}
        <div className="px-5 py-3.5 border-t border-slate-200/50 bg-slate-50/50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newId}
              onChange={e => { setNewId(e.target.value); setError(null); }}
              onKeyDown={handleKeyDown}
              placeholder="Enter FordShare ID..."
              className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0014cc]/20 focus:border-[#0014cc] transition-colors"
              disabled={isLoading}
              autoFocus
            />
            <button
              onClick={handleAdd}
              disabled={isLoading || !newId.trim()}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[#0014cc] rounded-lg hover:bg-[#0010a3] transition-colors disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>
          {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
